<template>
    <div ref="transferForm">
        <transfer-booking-forms-location-selection
            :point-selected="pointSelectedDeparture"
            :transfer-points-list="getTransferPointsList('departure')"
            @change="selectPointFromList($event, 'departure')"
        />
        <component
            :is="departureComponent"
            v-if="departureComponent"
            ref="departure"
            v-model="departureFormValid"
            point-type="departure"
            :disabled="disabled"
            :hotels="transferInfo && transferInfo.allowedHotelsDeparture"
            :product="product"
            :search-request="searchRequest"
            :point="pointSelectedDeparture"
            :request="request"
            :flight-service="flightService"
            :transfer-info="transferInfo"
            :departure-point-name="departurePoint.destinationPointName"
            :is-return-transfer="isReturnTransfer"
            :offer="offer"
            :disable-required-rule="disableRequiredRule"
            class="mb-5"
            @change="change"
            @change-location="changeLocation"
        />
        <v-divider class="my-8" />
        <transfer-details-form
            v-if="offer"
            ref="details"
            v-model="detailsFormValid"
            :disabled="disabled"
            :offer="offer"
            data-cy="transfer-details-form"
            class="mb-5"
            :request="request"
            :disable-required-rule="disableRequiredRule"
            @change="change"
        />
        <v-divider class="my-8" />
        <transfer-booking-forms-location-selection
            :point-selected="pointSelectedArrival"
            :transfer-points-list="getTransferPointsList('arrival')"
            @change="selectPointFromList($event, 'arrival')"
        />
        <component
            :is="arrivalComponent"
            v-if="arrivalComponent"
            ref="arrival"
            v-model="arrivalFormValid"
            point-type="arrival"
            :disabled="disabled"
            :hotels="transferInfo && transferInfo.allowedHotelsArrival"
            :product="product"
            :search-request="searchRequest"
            :point="arrivalPoint"
            :request="request"
            :transfer-info="transferInfo"
            :flight-service="flightService"
            :arrival-point-name="arrivalPoint.destinationPointName"
            :is-return-transfer="isReturnTransfer"
            :offer="offer"
            :disable-required-rule="disableRequiredRule"
            @change="change"
            @change-location="changeLocation"
        />
    </div>
</template>

<script lang="ts">
    import {Component, Emit, Prop, VModel, Vue, Watch} from 'vue-facing-decorator'
    import TransferDetailsForm from '@/components/transfers/booking/forms/TransferDetailsForm.vue'
    import TransferAirportForm from '@/components/transfers/booking/forms/TransferAirportForm.vue'
    import TransferHotelForm from '@/components/transfers/booking/forms/TransferHotelForm.vue'
    import TransferAddressForm from '@/components/transfers/booking/forms/TransferAddressForm.vue'
    import TransferTrainStationForm from '@/components/transfers/booking/forms/TransferTrainStationForm.vue'
    import TransferManualForm from '@/components/transfers/booking/forms/TransferManualForm.vue'
    import TransferCruisePortForm from '@/components/transfers/booking/forms/TransferCruisePortForm.vue'
    import TransferPositionForm from '@/components/transfers/booking/forms/TransferPositionForm.vue'
    import TransferBookingFormsLocationSelection from '@/components/transfers/booking/forms/TransferBookingFormsLocationSelection.vue'

    @Component({
        components: {
            TransferAirportForm,
            TransferHotelForm,
            TransferAddressForm,
            TransferTrainStationForm,
            TransferManualForm,
            TransferCruisePortForm,
            TransferDetailsForm,
            TransferPositionForm,
            TransferBookingFormsLocationSelection,
        },
    })
    export class TransferBookingForms extends Vue {
        @VModel() model
        @Prop({required: true}) product
        @Prop() transferInfo
        @Prop({required: true}) offer
        @Prop({required: true}) disabled
        //TODO searchRequest is only for dateTime
        @Prop({required: true}) searchRequest
        @Prop({required: true}) departurePoint
        @Prop({required: true}) arrivalPoint
        @Prop({required: true}) request
        @Prop() productBookingFields
        @Prop() flightService
        @Prop({type: Boolean, default: false}) isReturnTransfer
        @Prop({default: false, type: Boolean}) disableRequiredRule

        departureFormValid = false
        detailsFormValid = false
        arrivalFormValid = false
        formRefs = []

        pointSelectedDeparture = null
        pointSelectedArrival = null

        @Emit()
        change({prop, val}) {
            return {prop, val}
        }

        @Emit()
        changeLocation({prop, val, pointType}) {
            return {prop, val, pointType}
        }

        @Watch('valid')
        onValidChange(valid) {
            this.model = valid
        }

        async mounted() {
            this.pointSelectedDeparture = clone(this.departurePoint)
            this.pointSelectedArrival = clone(this.arrivalPoint)

            await this.$nextTick()

            this.formRefs = [
                this.$refs.departure,
                ...(this.$refs.details ? [this.$refs.details] : []),
                this.$refs.arrival,
            ]
        }

        pointComponent(point, pointType) {
            if (!point?.type) {
                return null
            }

            switch (point.type) {
                case 'airport':
                    return resolveComponent('TransferAirportForm')
                case 'city':
                    if (
                        this.transferInfo?.transferPoints?.find(t => t.pointType === pointType && t.type === 'MANUAL')
                    ) {
                        return resolveComponent('TransferManualForm')
                    }

                    return this.transferInfo?.[`allowedHotels${pointType.charAt(0).toUpperCase()}${pointType.slice(1)}`]
                        ? resolveComponent('TransferHotelForm')
                        : resolveComponent('TransferAddressForm')
                case 'trainStation':
                case 'train':
                case 'station':
                    return resolveComponent('TransferTrainStationForm')
                case 'cruisePort':
                case 'port':
                    return resolveComponent('TransferCruisePortForm')
                case 'hotel':
                case 'address':
                    return this.transferInfo?.[`allowedHotels${pointType.charAt(0).toUpperCase()}${pointType.slice(1)}`]
                        ? resolveComponent('TransferHotelForm')
                        : resolveComponent('TransferAddressForm')
                case 'manual':
                    return resolveComponent('TransferManualForm')
                case 'position':
                    return resolveComponent('TransferPositionForm')
            }
        }

        getTransferPointsList(pointType) {
            return this.transferInfo?.transferPoints
                .filter(el => el.pointType === pointType)
                .filter(el => el.type === 'HOTEL' || el.type === 'MANUAL')
        }

        async selectPointFromList(point, pointDirection) {
            const typeOfPoint = point.type.toLowerCase()

            const cityId =
                this[`${pointDirection}Point`].type === 'city'
                    ? this[`${pointDirection}Point`].id
                    : this[`${pointDirection}Point`].type === 'hotel'
                      ? this[`${pointDirection}Point`].cityId
                      : null

            transfersStore.SET_PREPARE_BOOK_REQUEST_PROP({
                prop: pointDirection,
                val: {type: typeOfPoint},
            })
            this[`pointSelected${pointDirection.charAt(0).toUpperCase()}${pointDirection.slice(1)}`] = null
            await this.$nextTick
            this[`pointSelected${pointDirection.charAt(0).toUpperCase()}${pointDirection.slice(1)}`] = {
                ...point,
                type: typeOfPoint,
                ...(typeOfPoint === 'hotel' && cityId && {cityId}),
            }
        }

        get departureComponent() {
            return this.pointComponent(this.pointSelectedDeparture, 'departure')
        }

        get arrivalComponent() {
            return this.pointComponent(this.pointSelectedArrival, 'arrival')
        }

        get valid() {
            return this.departureFormValid && this.detailsFormValid && this.arrivalFormValid
        }
    }

    export default TransferBookingForms
</script>
